<script setup>
import { computed, ref } from 'vue';
import { useI18n } from 'vue-i18n';
import { useRoute, useRouter } from 'vue-router';
import { useStore, useMapGetter } from 'dashboard/composables/store';
import { useAlert } from 'dashboard/composables';
import { emitter } from 'shared/helpers/mitt';
import { BUS_EVENTS } from 'shared/constants/busEvents';
import Button from 'dashboard/components-next/button/Button.vue';
import DropdownMenu from 'dashboard/components-next/dropdown-menu/DropdownMenu.vue';
import ComposeConversation from 'dashboard/components-next/NewConversation/ComposeConversation.vue';
import { processContactableInboxes } from 'dashboard/components-next/NewConversation/helpers/composeConversationHelper.js';
import {
  INBOX_TYPES,
  getInboxIconByType,
  getInboxSource,
} from 'dashboard/helper/inbox';

const props = defineProps({
  contact: {
    type: Object,
    default: () => ({}),
  },
});

const { t } = useI18n();
const route = useRoute();
const router = useRouter();
const store = useStore();
const showMenu = ref(false);
const isPreparingModal = ref(false);

const inboxById = useMapGetter('inboxes/getInboxById');
const getContactById = useMapGetter('contacts/getContactById');
const contactsUiFlags = useMapGetter('contacts/getUIFlags');
const conversationsUiFlags = useMapGetter('contactConversations/getUIFlags');
const contactConversations = useMapGetter(
  'contactConversations/getAllConversationsByContactId'
);

const contactId = computed(() => Number(props.contact?.id || 0));
const effectiveContact = computed(() => {
  if (!contactId.value) {
    return props.contact || {};
  }

  return getContactById.value(contactId.value) || props.contact || {};
});

const resolvedContactId = computed(() =>
  Number(effectiveContact.value?.id || props.contact?.id || 0)
);

const currentConversationId = computed(() => Number(route.params.id || 0));
const currentInboxId = computed(() => Number(route.params.inboxId || 0));

const contactableInboxes = computed(() =>
  processContactableInboxes(
    effectiveContact.value?.contactInboxes ||
      effectiveContact.value?.contact_inboxes ||
      []
  )
);

const conversations = computed(() =>
  resolvedContactId.value
    ? contactConversations.value(resolvedContactId.value) || []
    : []
);

const contactAdditionalAttributes = computed(
  () =>
    effectiveContact.value?.additionalAttributes ||
    effectiveContact.value?.additional_attributes ||
    {}
);

const contactSocialProfiles = computed(
  () =>
    contactAdditionalAttributes.value.socialProfiles ||
    contactAdditionalAttributes.value.social_profiles ||
    {}
);

const contactEmail = computed(() => effectiveContact.value?.email || '');
const contactPhone = computed(
  () =>
    effectiveContact.value?.phoneNumber ||
    effectiveContact.value?.phone_number ||
    ''
);

const contactInstagramUsername = computed(() => {
  const username =
    contactSocialProfiles.value.instagram ||
    contactAdditionalAttributes.value.socialInstagramUserName ||
    contactAdditionalAttributes.value.social_instagram_user_name;

  return username ? String(username).replace(/^@/, '') : '';
});

const latestConversationByInbox = computed(() => {
  return conversations.value.reduce((accumulator, conversation) => {
    const inboxId = Number(conversation?.inboxId);

    if (!inboxId || accumulator.has(inboxId)) {
      return accumulator;
    }

    accumulator.set(inboxId, conversation);
    return accumulator;
  }, new Map());
});

const isLoadingChannels = computed(() => {
  return (
    contactsUiFlags.value.isFetchingInboxes ||
    conversationsUiFlags.value.isFetching ||
    isPreparingModal.value
  );
});

const contactableInboxById = computed(() => {
  return contactableInboxes.value.reduce((accumulator, inbox) => {
    if (inbox?.id) {
      accumulator.set(Number(inbox.id), inbox);
    }

    return accumulator;
  }, new Map());
});

const readableChannelName = inbox => {
  if (!inbox?.channelType) {
    return t('CONTACT_PANEL.QUICK_REPLY.FALLBACK_CHANNEL');
  }

  switch (inbox.channelType) {
    case INBOX_TYPES.WHATSAPP:
      return 'WhatsApp';
    case INBOX_TYPES.TWILIO:
      return inbox.medium === 'whatsapp' ? 'WhatsApp' : 'SMS';
    case INBOX_TYPES.EMAIL:
      return 'Email';
    case INBOX_TYPES.TELEGRAM:
      return 'Telegram';
    case INBOX_TYPES.INSTAGRAM:
      return 'Instagram';
    case INBOX_TYPES.FB:
      return 'Messenger';
    case INBOX_TYPES.API:
      return 'API Inbox';
    case INBOX_TYPES.WEB:
      return 'Live chat';
    case INBOX_TYPES.SMS:
      return 'SMS';
    case INBOX_TYPES.LINE:
      return 'LINE';
    case INBOX_TYPES.TIKTOK:
      return 'TikTok';
    default:
      return t('CONTACT_PANEL.QUICK_REPLY.FALLBACK_CHANNEL');
  }
};

const inboxDescriptor = inbox => {
  const source = getInboxSource(inbox?.channelType, inbox?.phoneNumber, inbox);
  return inbox?.name || source || '';
};

const formatInboxLabel = inbox => {
  const channelName = readableChannelName(inbox);
  const descriptor = inboxDescriptor(inbox);

  return descriptor ? `${channelName} · ${descriptor}` : channelName;
};

const shortenSourceId = sourceId => {
  if (!sourceId) {
    return '';
  }

  const normalizedSourceId = String(sourceId);

  if (normalizedSourceId.length <= 20) {
    return normalizedSourceId;
  }

  return `${normalizedSourceId.slice(0, 8)}…${normalizedSourceId.slice(-6)}`;
};

const normalizeSourceIdForDisplay = (sourceId, inbox) => {
  if (!sourceId) {
    return '';
  }

  if (inbox?.channelType === INBOX_TYPES.TWILIO) {
    return String(sourceId).replace(/^whatsapp:/, '');
  }

  return String(sourceId);
};

const formatInboxDescription = (inbox, sourceId = '') => {
  const normalizedSourceId = normalizeSourceIdForDisplay(sourceId, inbox);

  switch (inbox?.channelType) {
    case INBOX_TYPES.EMAIL:
      return normalizedSourceId || contactEmail.value || '';
    case INBOX_TYPES.WHATSAPP:
    case INBOX_TYPES.SMS:
    case INBOX_TYPES.TELEGRAM:
      return normalizedSourceId || contactPhone.value || '';
    case INBOX_TYPES.TWILIO:
      return normalizedSourceId || contactPhone.value || '';
    case INBOX_TYPES.INSTAGRAM:
      return contactInstagramUsername.value
        ? `@${contactInstagramUsername.value}`
        : '';
    case INBOX_TYPES.API:
      return (
        contactPhone.value ||
        contactEmail.value ||
        `ID ${shortenSourceId(sourceId)}`
      );
    case INBOX_TYPES.WEB:
      return (
        contactEmail.value ||
        contactPhone.value ||
        `ID ${shortenSourceId(sourceId)}`
      );
    default:
      return normalizedSourceId || contactEmail.value || contactPhone.value || '';
  }
};

const resolveInboxDetails = inboxId => {
  return (
    contactableInboxById.value.get(Number(inboxId)) || inboxById.value(inboxId)
  );
};

const resolveInboxSourceId = inboxId => {
  return contactableInboxById.value.get(Number(inboxId))?.sourceId || '';
};

const existingChannelItems = computed(() => {
  return Array.from(latestConversationByInbox.value.entries())
    .map(([inboxId, conversation]) => {
      if (
        Number(conversation?.id) === currentConversationId.value &&
        Number(inboxId) === currentInboxId.value
      ) {
        return null;
      }

      const inbox = resolveInboxDetails(inboxId);
      const sourceId = resolveInboxSourceId(inboxId);

      if (!inbox?.id) {
        return null;
      }

      return {
        action: 'open-existing',
        value: `conversation-${inboxId}`,
        label: formatInboxLabel(inbox),
        description: formatInboxDescription(inbox, sourceId),
        icon: getInboxIconByType(inbox.channelType, inbox.medium, 'line'),
        conversationId: conversation.id,
        inboxId: Number(inboxId),
      };
    })
    .filter(Boolean);
});

const existingInboxIds = computed(() => {
  return new Set(existingChannelItems.value.map(item => item.inboxId));
});

const newConversationItems = computed(() => {
  return contactableInboxes.value
    .filter(inbox => inbox?.id && !existingInboxIds.value.has(Number(inbox.id)))
    .map(inbox => ({
      action: 'compose-new',
      value: `compose-${inbox.id}`,
      label: formatInboxLabel(inbox),
      description: formatInboxDescription(inbox, inbox.sourceId),
      icon: getInboxIconByType(inbox.channelType, inbox.medium, 'line'),
      inbox,
    }));
});

const allChannelActions = computed(() => {
  return [...existingChannelItems.value, ...newConversationItems.value];
});

const menuSections = computed(() => {
  const sections = [];

  if (existingChannelItems.value.length) {
    sections.push({
      title: t('CONTACT_PANEL.QUICK_REPLY.SECTIONS.EXISTING'),
      items: existingChannelItems.value,
    });
  }

  if (newConversationItems.value.length) {
    sections.push({
      title: t('CONTACT_PANEL.QUICK_REPLY.SECTIONS.NEW'),
      items: newConversationItems.value,
    });
  }

  return sections;
});

const setMenuVisibility = nextValue => {
  showMenu.value = Boolean(nextValue);
};

const closeComposeConversationModal = () => {
  emitter.emit(BUS_EVENTS.NEW_CONVERSATION_MODAL, false);
};

const openComposeConversationModal = (toggleCompose, options = {}) => {
  setMenuVisibility(false);
  toggleCompose({
    contact: effectiveContact.value || props.contact || null,
    ...options,
  });
  emitter.emit(BUS_EVENTS.NEW_CONVERSATION_MODAL, true);
};

const openConversation = (conversationId, inboxId) => {
  setMenuVisibility(false);

  router.push({
    name: 'inbox_view_conversation',
    params: {
      accountId: route.params.accountId,
      inboxId,
      type: 'conversation',
      id: conversationId,
    },
  });
};

const preloadChannelData = async () => {
  if (!resolvedContactId.value) {
    return;
  }

  isPreparingModal.value = true;

  try {
    await Promise.allSettled([
      store.dispatch('contacts/fetchContactableInbox', resolvedContactId.value),
      store.dispatch('contactConversations/get', resolvedContactId.value),
    ]);
  } finally {
    isPreparingModal.value = false;
  }
};

const handleMenuAction = (item, toggleCompose) => {
  if (item.action === 'open-existing') {
    openConversation(item.conversationId, item.inboxId);
    return;
  }

  if (item.action === 'compose-new') {
    openComposeConversationModal(toggleCompose, { inbox: item.inbox });
  }
};

const handleTriggerClick = async toggleCompose => {
  if (!resolvedContactId.value) {
    useAlert(t('CONTACT_PANEL.QUICK_REPLY.NO_CHANNELS'));
    return;
  }

  await preloadChannelData();

  if (!allChannelActions.value.length) {
    useAlert(t('CONTACT_PANEL.QUICK_REPLY.NO_CHANNELS'));
    return;
  }

  if (allChannelActions.value.length === 1) {
    handleMenuAction(allChannelActions.value[0], toggleCompose);
    return;
  }

  setMenuVisibility(!showMenu.value);
};
</script>

<template>
  <ComposeConversation
    :contact-id="String(resolvedContactId || effectiveContact.id || contact.id)"
    is-modal
    @close="closeComposeConversationModal"
  >
    <template #trigger="{ toggle }">
      <div
        v-on-clickaway="() => setMenuVisibility(false)"
        class="relative"
      >
        <Button
          icon="i-ph-paper-plane-tilt"
          slate
          faded
          sm
          :is-loading="isLoadingChannels"
          class="max-w-full"
          @click="handleTriggerClick(toggle)"
        >
          <span class="min-w-0 truncate">
            {{ $t('CONTACT_PANEL.QUICK_REPLY.BUTTON') }}
          </span>
          <span
            v-if="allChannelActions.length > 1"
            class="i-ph-caret-down text-xs flex-shrink-0"
          />
        </Button>

        <DropdownMenu
          v-if="showMenu"
          :menu-sections="menuSections"
          class="top-full mt-1 min-w-[264px] ltr:left-0 rtl:right-0"
          @action="item => handleMenuAction(item, toggle)"
        />
      </div>
    </template>
  </ComposeConversation>
</template>
